import React, { useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { useUser } from '@src/state/user/hooks';
import { storage } from '@uhg-abyss/web/tools/storage';
import { V2DataTable } from '@uhg-abyss/web/ui/DataTable';
import { useDataTableV2 } from '@uhg-abyss/web/hooks/useDataTable';
import { event as TaggingEvent } from '@uhg-abyss/web/tools/event';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { V2Badge } from '@uhg-abyss/web/ui/Badge';
import { Button } from '@src/common/Button';
import { Loading } from '@src/common/Loading';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useTinRegistration } from '@src/state/tinRegistration';
import { V2Text } from '@uhg-abyss/web/ui/Text';
import { Layout } from '@uhg-abyss/web/ui/Layout';

const ActionButton = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  gap: '4px',
});

export const SavedRegistrations = ({
  tinDraftResult,
  setTinDraft,
  getFormatedDate,
  onYes,
}) => {
  const { deleteSession, disableSaveAsDraft } = useTinRegistration.actions();
  const userInfo = useUser.state();
  const { uuid } = userInfo;
  const isSuperUser = storage.local.get(uuid);
  const { deleteSessionSaga } = useTinRegistration.state();

  const handleDeleteSession = (row, index) => {
    const { billingTin, uuid } = row;
    deleteSession({ billingTin, index }, (res) => {
      setTinDraft((prevTinDraft) => {
        const updatedTin = [...prevTinDraft];
        updatedTin.splice(index, 1);
        const formattedTinResults = getFormatedDate(updatedTin);
        return formattedTinResults;
      });
    });
    TaggingEvent('DELETE_REGISTRATION', { uuid, billingTin });
    const eventData = {
      action: 'Remove',
    };
    TaggingEvent('EAPM_TRANSACTION_CONTINUE', eventData);
  };

  const handleEditSession = (row) => {
    if (row) {
      disableSaveAsDraft(false);
      onYes(row);
    }
    const eventData = {
      action: 'Edit',
    };
    TaggingEvent('EAPM_TRANSACTION_CONTINUE', eventData);
  };

  const getColumns = () => {
    return [
      {
        id: 'billingTin',
        header: 'TIN',
        accessorKey: 'billingTin',
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row?.w9Details?.name,
      },
      {
        id: 'startDate',
        header: 'Date started',
        accessorKey: 'startDate',
      },
      {
        id: 'expiryDate',
        header: 'Registration expires',
        accessorKey: 'expiryDate',
        cell: ({ row }) => {
          const { original } = row;
          const { expiryDate, expiryOn } = original;
          return (
            <Layout.Group
              css={{ gap: '8px', alignItems: 'center' }}
              id="expiry-wrapper"
            >
              {expiryDate}
              <V2Badge variant="neutral" id="dashboard-savedDraftText" outline>
                {expiryOn} days left
              </V2Badge>
            </Layout.Group>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const { original, index } = row;
          return (
            <Layout.Group
              css={{ justifyContent: 'space-evenly', width: '100%' }}
            >
              <ActionButton id="editsavedicon">
                <Button
                  variant="tertiary"
                  id="edit-save-button"
                  size="size400"
                  disabled={!!isSuperUser}
                  onClick={() => handleEditSession(original)}
                >
                  Edit
                </Button>
                <IconSymbol
                  icon="edit"
                  size="18px"
                  color={isSuperUser ? '$core.color.neutral.60' : undefined}
                  variant="outlined"
                />
              </ActionButton>

              <ActionButton id="removesavedIcon">
                <Button
                  variant="tertiary"
                  id="remove-save-button"
                  size="size400"
                  disabled={!!isSuperUser}
                  onClick={() => handleDeleteSession(original, index)}
                >
                  Delete
                </Button>
                <IconSymbol
                  icon="delete"
                  size="18px"
                  color={isSuperUser ? '$core.color.neutral.60' : undefined}
                  variant="outlined"
                />
              </ActionButton>
            </Layout.Group>
          );
        },
      },
    ];
  };

  const columns = getColumns();

  const tableData = (tinDraftResult || []).map((row, index) => ({
    ...row,
    uniqueId:
      row?.uniqueId || `${row?.uuid || 'uuid'}-${row?.billingTin || index}`,
  }));

  const dataTableProps = useDataTableV2({
    initialData: tableData,
    initialColumns: columns,
    tableConfig: {
      enableSorting: false,
    },
    paginationConfig: {
      enablePagination: false,
    },
  });

  useEffect(() => {
    const pageName = 'epvsecure:home';
    const billingTin = '';
    const siteSectionLevel = {
      siteSectionLevel1: 'home',
      siteSectionLevel2: '',
      siteSectionLevel3: '',
      siteSectionLevel4: '',
      siteSectionLevel5: '',
    };
    TaggingEvent('ON_PAGE_LOAD', {
      userInfo,
      pageName,
      billingTin,
      siteSectionLevel,
    });

    dataTableProps.setColumns(columns, true);
    dataTableProps.setData(tableData, true);
  }, [tinDraftResult]);

  return (
    <Loading isLoading={deleteSessionSaga.isLoading}>
      {tinDraftResult && tinDraftResult.length > 0 ? (
        <V2DataTable hideHeader tableState={dataTableProps}>
          <V2DataTable.Table />
        </V2DataTable>
      ) : (
        <V2Text
          css={{
            display: 'block',
            color: '$primary1',
            fontSize: '20px',
            fontWeight: 700,
            textAlign: 'center',
          }}
          id="no-drafts-message"
        >
          No drafts yet
        </V2Text>
      )}
    </Loading>
  );
};

SavedRegistrations.propTypes = {
  tinDraftResult: PropTypes.func.isRequired,
  setTinDraft: PropTypes.func.isRequired,
  getFormatedDate: PropTypes.func.isRequired,
  onYes: PropTypes.func.isRequired,
};
